import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { Building2, Users, Activity, AlertTriangle, TrendingUp, Clock } from 'lucide-react';

const ACTION_LABELS = {
  login_success: 'Logged in',
  login_failure: 'Failed login',
  user_created: 'Created user',
  user_disabled: 'Disabled user',
  user_enabled: 'Enabled user',
  user_deleted: 'Deleted user',
  password_reset_forced: 'Force password reset',
  mfa_reset: 'Reset MFA',
  role_changed: 'Changed role',
  organisation_suspended: 'Suspended org',
  organisation_activated: 'Activated org',
  organisation_created: 'Created org',
  impersonation_start: 'Started impersonation',
  impersonation_end: 'Ended impersonation',
};

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const r = await adminApi.getDashboard();
      setStats(r.data);
    } catch (error) {
      console.error('Failed to load admin dashboard:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <AdminLayout title="Dashboard"><div className="text-[#4E6974]">Loading...</div></AdminLayout>;
  if (!stats) return <AdminLayout title="Dashboard"><div className="text-red-500">Failed to load dashboard</div></AdminLayout>;

  const cards = [
    { label: 'Total Organisations', value: stats.total_organisations, icon: Building2, color: '#0097A7' },
    { label: 'Total Users', value: stats.total_users, icon: Users, color: '#4DD0E1' },
    { label: 'Active (30d)', value: stats.active_users_30d, icon: Activity, color: '#26A69A' },
    { label: 'Failed Logins (24h)', value: stats.failed_logins_24h, icon: AlertTriangle, color: stats.failed_logins_24h > 0 ? '#FF6B35' : '#B0BEC5' },
    { label: 'Sign-ups (7d)', value: stats.signups_this_week, icon: TrendingUp, color: '#7C4DFF' },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats-grid">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-[#E8ECF0] p-4" data-testid={`admin-stat-${c.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <c.icon className="h-4 w-4" style={{ color: c.color }} />
              <span className="text-xs text-[#4E6974]">{c.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#0A2533]">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Orgs by tier */}
      {Object.keys(stats.organisations_by_tier).length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8ECF0] p-5 mb-6">
          <h2 className="text-sm font-semibold text-[#0A2533] mb-3">Organisations by tier</h2>
          <div className="flex gap-4">
            {Object.entries(stats.organisations_by_tier).map(([tier, count]) => (
              <div key={tier} className="text-center">
                <p className="text-lg font-bold text-[#0A2533]">{count}</p>
                <p className="text-xs text-[#4E6974] capitalize">{tier}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent audit */}
      <div className="bg-white rounded-xl border border-[#E8ECF0] p-5" data-testid="admin-recent-audit">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#0A2533]">Recent audit log</h2>
          <Link to="/admin/audit-log" className="text-xs text-[#0097A7] hover:underline">View all</Link>
        </div>
        <div className="space-y-2">
          {stats.recent_audit.length === 0 && <p className="text-sm text-[#B0BEC5]">No audit entries yet</p>}
          {stats.recent_audit.slice(0, 10).map(entry => (
            <div key={entry.id} className="flex items-center gap-3 py-2 border-b border-[#F5F5F5] last:border-0">
              <Clock className="h-3.5 w-3.5 text-[#B0BEC5] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-[#0A2533]">{ACTION_LABELS[entry.action] || entry.action}</span>
                {entry.ip_address && <span className="text-xs text-[#B0BEC5] ml-2">from {entry.ip_address}</span>}
              </div>
              <span className="text-xs text-[#B0BEC5] flex-shrink-0">{new Date(entry.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
